import React, {useState} from 'react';
import Icon from '@/shared/components/Icon';
import {TestEducation} from '@/shared/data/testEducation';
import ProgressBar from '@/shared/charts/ProgressBar';
import TestingHistory from '@/shared/charts/TestingHistory';
import {BIOMARKER_VALUE_COLOR, getStatusClass, getStatusColor} from '@/shared/constants/colors';
import {severityTier} from '@/shared/biomarkers/classify';
import {useBiomarkerZones} from '@/shared/hooks/useBiomarkerZones';
import {resolveDisplayRule} from '@/shared/biomarkers/resolveDisplayRule';
import {ruleToConfig, valueOnlyReferenceText} from '@/shared/biomarkers/ruleToConfig';
import type {BiomarkerDisplayRule} from '@/shared/biomarkers/displayRuleTypes';
import {buildTestingHistoryChartData, type TestingHistoryPoint} from '@/shared/utils/resultUtils';
import {isBinaryTest, getBinaryTestStatus, formatResultValue, isTestNotPerformed} from '@/shared/utils/biomarkerUtils';
import {BiomarkerStatus} from '@/shared/types/biomarker';

/**
 * Helper function to format text with bold titles (lines ending with ':')
 */
const formatTextWithBoldTitles = (text: string): React.ReactNode => {
    const lines = text.split('\n');
    return lines.map((line, index) => {
        // Check if line ends with ':' (title line)
        const colonMatch = line.match(/^(.+:)(.*)$/);
        if (colonMatch) {
            const [, title, rest] = colonMatch;
            return (
                <React.Fragment key={index}>
                    <strong>{title}</strong>
                    {rest}
                    {index < lines.length - 1 && '\n'}
                </React.Fragment>
            );
        }
        return (
            <React.Fragment key={index}>
                {line}
                {index < lines.length - 1 && '\n'}
            </React.Fragment>
        );
    });
};

type TestingHistoryData = TestingHistoryPoint;

interface BiomarkerChartCardProps {
    name: string;
    /** Parsed numeric value, or `null` for a non-numeric result
     *  (e.g. "Test Not Performed", "No Result", "TNP"). When null the
     *  headline shows the verbatim lab text, the slider indicator is
     *  hidden, and the trend gaps this reading instead of plotting 0. */
    value: number | null;
    units: string;
    status?: BiomarkerStatus;
    rangeMin?: number;
    rangeMax?: number;
    history?: TestingHistoryData[];
    lastTestDate?: string;
    className?: string;
    education?: TestEducation | null;
    result?: any;
    recommendations?: any[];
    gender?: 'male' | 'female';
    /** Admin-managed display rules (see useDisplayRules); defaults to none. */
    rules?: BiomarkerDisplayRule[];
}

const BiomarkerChartCard: React.FC<BiomarkerChartCardProps> = ({
    name,
    value,
    units,
    status,
    rangeMin = 0,
    rangeMax = 100,
    history = [],
    lastTestDate,
    className = '',
    education,
    result,
    recommendations = [],
    gender,
    rules = [],
}) => {
    const [expanded, setExpanded] = useState(false);

    // Check for an admin-managed display rule (LOINC-first, falling back to
    // test name; sex-aware for HDL Direct/Uric Acid/etc.)
    const displayRule = resolveDisplayRule(rules, {
        loinc: result?.loinc,
        assayCode: result?.test_code,
        testName: result?.test_name ?? name,
        sex: gender,
    });
    const customConfig = displayRule ? ruleToConfig(displayRule) : null;
    const isValueOnly = valueOnlyReferenceText(displayRule) !== null;

    // Check if this is a binary test (Positive/Negative)
    const isBinary = result?.result ? isBinaryTest(result.result) : false;
    const binaryStatus = isBinary ? getBinaryTestStatus(result.result) : null;

    const {progressBarSections, optimalZoneInfo, indicatorPercent, hasValue} = useBiomarkerZones({
        rangeMin: rangeMin ?? 0,
        rangeMax: rangeMax ?? 100,
        rawRefRange: result?.refRange ?? '',
        customConfig,
        isBinary,
        // The hook treats null/undefined identically (no finite value);
        // normalize to undefined to match its `value?: number` signature.
        value: value ?? undefined,
        binaryStatus,
    });

    // Find recommendations that match this biomarker
    const getMatchingRecommendations = () => {
        if (!recommendations || !result) return [];

        return recommendations.filter((rec: any) => {
            if (!rec.biomarkers || rec.biomarkers.length === 0) return false;

            return rec.biomarkers.some((biomarker: any) => {
                // Prefer canonical assay_code matching against the lab
                // result's test_code (assay_catalog.code).
                const resultCode = result.test_code;
                const biomarkerCode = biomarker.assay_code;
                if (resultCode && biomarkerCode) {
                    return resultCode === biomarkerCode;
                }

                // Fallback to name matching (legacy)
                const testName = result.test_name?.toLowerCase() || '';
                const biomarkerType = biomarker.test_type?.toLowerCase() || '';
                return testName.includes(biomarkerType) || biomarkerType.includes(testName);
            });
        });
    };

    const matchingRecommendations = getMatchingRecommendations();

    // Status text color + severity-tier icon both derive from the canonical
    // status→color / status→tier mappers so this card's status color and icon
    // always agree with the chip/chart/count colors elsewhere.
    const displayStatus: BiomarkerStatus = isBinary && binaryStatus ? binaryStatus : (status ?? 'Unknown');
    const statusTier = status ? severityTier(status) : 'unknown';

    return (
        <div
            className={`bg-white rounded-lg border border-gray-200 ${className} ${education ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}>
            <div className="p-3 lg:p-5" onClick={() => education && setExpanded(!expanded)}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-2 sm:gap-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        <h3 className="text-sm lg:text-base font-medium text-gray-900">{name}</h3>
                        {!isValueOnly && status && (
                            <div className="flex items-center gap-2">
                                <span className={`text-xs font-medium ${getStatusClass(displayStatus, 'text')}`}>
                                    {displayStatus}
                                </span>
                                {statusTier === 'normal' ? (
                                    <div className="w-4 h-4 rounded-full bg-white border-2 border-mld-status-normal flex items-center justify-center">
                                        <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                                            <path
                                                d="M1 3L2.5 4.5L7 0.5"
                                                stroke={getStatusColor(status)}
                                                strokeWidth="1.2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </div>
                                ) : statusTier === 'critical' ? (
                                    <div
                                        className={`w-4 h-4 rounded-full bg-white border-2 ${getStatusClass(status, 'border')} flex items-center justify-center`}>
                                        <svg width="2" height="10" viewBox="0 0 2 10" fill="none">
                                            <path
                                                d="M1 0V6.5M1 8.5V9.5"
                                                stroke={getStatusColor(status)}
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </div>
                                ) : statusTier === 'borderline' ? (
                                    <div
                                        className={`w-4 h-4 rounded-full bg-white border-2 ${getStatusClass(status, 'border')} flex items-center justify-center`}>
                                        <svg width="2" height="10" viewBox="0 0 2 10" fill="none">
                                            <path
                                                d="M1 0V6.5M1 8.5V9.5"
                                                stroke={getStatusColor(status)}
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </div>
                                ) : null}
                            </div>
                        )}
                        {matchingRecommendations.map((rec: any, index: number) => (
                            <span
                                key={rec.id || index}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium self-start bg-mld-warning/5 text-mld-warning border border-mld-warning/30"
                                title={rec.content}>
                                {rec.name}
                            </span>
                        ))}
                    </div>
                    {education && (
                        <div className="flex items-center">
                            {expanded ? (
                                <Icon name="chevron-up" className="h-5 w-5 text-gray-500" />
                            ) : (
                                <Icon name="chevron-down" className="h-5 w-5 text-gray-500" />
                            )}
                        </div>
                    )}
                </div>

                <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 lg:gap-6">
                    {/* Left side - Current value and range */}
                    <div>
                        <div className="mb-4">
                            {isTestNotPerformed(result?.result) ? (
                                <span className="text-xl lg:text-2xl font-semibold text-gray-400">Not Tested</span>
                            ) : (
                                <span className="text-2xl lg:text-3xl font-bold" style={{color: BIOMARKER_VALUE_COLOR}}>
                                    {isBinary && result?.result
                                        ? result.result
                                        : (() => {
                                              const rounded = Number(Number(value).toFixed(2));
                                              return rounded === Math.floor(rounded)
                                                  ? rounded.toString()
                                                  : rounded.toString();
                                          })()}
                                </span>
                            )}
                            {/* Hide the units suffix for non-numeric results — "No Result mg/dL" reads wrong. */}
                            {!isBinary && value !== null && <span className="text-sm text-gray-500 ml-2">{units}</span>}
                        </div>

                        {/* Range visualization using reusable ProgressBar */}
                        {!isValueOnly && (
                            <div className="mb-4">
                                <ProgressBar
                                    sections={progressBarSections}
                                    indicatorPosition={indicatorPercent}
                                    // Hide the position dot for non-numeric results — the colored
                                    // zones still render, but no dot implies a (missing) value.
                                    showIndicator={hasValue}
                                    className="mb-0"
                                />

                                {/* Range labels - different layout for binary vs numeric tests */}
                                {isBinary ? (
                                    /* Binary tests: show Negative/Positive labels centered in each zone */
                                    <div className="relative mt-6 text-xs text-gray-500">
                                        <div className="flex justify-between px-4">
                                            <span className="text-center" style={{width: '50%'}}>
                                                Negative
                                            </span>
                                            <span className="text-center" style={{width: '50%'}}>
                                                Positive
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    /* Numeric tests: show min/max values with dashed lines */
                                    <div className="relative mt-6 text-xs text-gray-500">
                                        {/* Label positioned at center of optimal zone */}
                                        <div className="relative mb-3 flex">
                                            <span
                                                className="absolute"
                                                style={{
                                                    left: `${optimalZoneInfo.startPercent + (optimalZoneInfo.endPercent - optimalZoneInfo.startPercent) / 2}%`,
                                                    transform: 'translateX(-50%)',
                                                }}>
                                                {optimalZoneInfo.label}
                                            </span>
                                        </div>
                                        <div className="absolute top-0 left-0 w-full flex">
                                            {/* Min value with dashed line */}
                                            <div
                                                className="absolute"
                                                style={{
                                                    left: `calc(${optimalZoneInfo.startPercent}% + 8px)`,
                                                    transform: 'translateX(-50%)',
                                                }}>
                                                <div className="flex flex-col items-center">
                                                    <div
                                                        style={{
                                                            width: '2px',
                                                            height: '16px',
                                                            borderLeft: '2px dashed #9CA3AF',
                                                        }}
                                                    />
                                                    <span className="mt-2">{optimalZoneInfo.minValue}</span>
                                                </div>
                                            </div>
                                            {/* Max value with dashed line */}
                                            <div
                                                className="absolute"
                                                style={{
                                                    left: `calc(${optimalZoneInfo.endPercent}% - 8px)`,
                                                    transform: 'translateX(-50%)',
                                                }}>
                                                <div className="flex flex-col items-center">
                                                    <div
                                                        style={{
                                                            width: '2px',
                                                            height: '16px',
                                                            borderLeft: '2px dashed #9CA3AF',
                                                        }}
                                                    />
                                                    <span className="mt-2">{optimalZoneInfo.maxValue}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Right side - Testing History (hidden for binary tests) */}
                    <div className="mt-4 lg:mt-0">
                        {isBinary ? (
                            /* Binary tests don't show testing history - just show the last tested date */
                            <div className="h-24 lg:h-32 flex items-center justify-center">
                                {lastTestDate && (
                                    <div className="text-sm text-gray-500">
                                        Last tested{' '}
                                        {new Date(lastTestDate).toLocaleDateString('en-US', {
                                            month: 'numeric',
                                            day: 'numeric',
                                            year: '2-digit',
                                        })}
                                    </div>
                                )}
                            </div>
                        ) : history && history.length > 0 ? (
                            (() => {
                                const chartData = buildTestingHistoryChartData({
                                    history,
                                    currentValue: value,
                                    currentDate: lastTestDate || result?.statusDate || result?.createdDate,
                                    maxHistoryPoints: 3,
                                });

                                return (
                                    <TestingHistory
                                        data={chartData}
                                        title="Testing History"
                                        height={120}
                                        showGrid={true}
                                        showVerticalLines={true}
                                        thresholds={{low: rangeMin || 0, high: rangeMax || 100}}
                                        className="text-xs"
                                        labelFontSize="16px"
                                    />
                                );
                            })()
                        ) : (
                            <>
                                <div className="mb-3">
                                    <h4 className="text-sm font-medium text-gray-700">Testing History</h4>
                                </div>
                                <div className="h-24 lg:h-32 flex items-center justify-center text-sm text-gray-500">
                                    No historical data available
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {lastTestDate && !isBinary && (
                    <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-400 text-right">
                        Last tested{' '}
                        {new Date(lastTestDate).toLocaleDateString('en-US', {
                            month: 'numeric',
                            day: 'numeric',
                            year: '2-digit',
                        })}
                    </div>
                )}
            </div>

            {/* Educational Information - Expanded Section */}
            {expanded && education && (
                <div className="border-t border-gray-200 bg-gray-50 px-3 lg:px-5 pb-3 lg:pb-5">
                    <div className="bg-white p-4 lg:p-6 rounded-lg shadow-sm mt-3 lg:mt-5">
                        <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 mt-1">
                                <div className="w-5 h-5 rounded-full bg-mld-info/10 flex items-center justify-center">
                                    <span className="text-mld-info text-xs font-bold">i</span>
                                </div>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-base font-medium text-gray-900 mb-3">{education.title}</h3>

                                <div className="text-sm text-gray-700 space-y-3">
                                    <p>{education.description}</p>

                                    {/* Always show relevant status-specific information */}
                                    {education.lowLevel && (
                                        <>
                                            <p>
                                                <strong>{education.lowLevel.title}</strong> -{' '}
                                                {education.lowLevel.description}
                                            </p>
                                            {education.lowLevel.symptoms && education.lowLevel.symptoms.length > 0 && (
                                                <>
                                                    <p>
                                                        <strong>
                                                            Symptoms of low {name.toLowerCase()} can include:
                                                        </strong>
                                                    </p>
                                                    <ul className="list-none space-y-1 ml-4">
                                                        {education.lowLevel.symptoms.map((symptom, index) => (
                                                            <li key={index} className="flex items-start">
                                                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 mr-2 flex-shrink-0"></span>
                                                                {symptom}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </>
                                            )}
                                        </>
                                    )}

                                    {education.highLevel && (
                                        <>
                                            <p>
                                                <strong>{education.highLevel.title}</strong> -{' '}
                                                {education.highLevel.description}
                                            </p>
                                            {education.highLevel.symptoms &&
                                                education.highLevel.symptoms.length > 0 && (
                                                    <>
                                                        <p>
                                                            <strong>
                                                                Symptoms of elevated {name.toLowerCase()} can include:
                                                            </strong>
                                                        </p>
                                                        <ul className="list-none space-y-1 ml-4">
                                                            {education.highLevel.symptoms.map((symptom, index) => (
                                                                <li key={index} className="flex items-start">
                                                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 mr-2 flex-shrink-0"></span>
                                                                    {symptom}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </>
                                                )}
                                        </>
                                    )}

                                    {education.normalLevel && (
                                        <p>
                                            <strong>{education.normalLevel.title}</strong> -{' '}
                                            {education.normalLevel.description}
                                        </p>
                                    )}

                                    {/* Additional information */}
                                    {education.additionalInfo && (
                                        <p className="whitespace-pre-line">
                                            {formatTextWithBoldTitles(education.additionalInfo)}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Lab notes if available */}
                        {result?.notes && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                                <div className="flex items-start space-x-2">
                                    <div className="flex-shrink-0 mt-1">
                                        <span className="text-blue-500">📝</span>
                                    </div>
                                    <div className="text-sm text-gray-700">{result.notes}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default React.memo(BiomarkerChartCard);
